// StudioContext.js

import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Utilities
import { SectionType } from 'utilities/Enums';
import { getSectionPath, isStudioPath } from 'utilities/Routing';

const StudioContext = createContext(null);

/**
 * StudioProvider
 *
 * Marker provider for Appdeck Studio route integration.
 */
export function StudioProvider({ children }) {
  return (
    <StudioContext.Provider value={true}>
      {children}
    </StudioContext.Provider>
  );
}

/**
 * useStudio
 *
 * Studio open state is derived from the /studio route.
 *
 * @returns {{ isStudioOpen: boolean, openStudio: function, closeStudio: function }}
 */
export function useStudio() {
  const inStudioProvider = useContext(StudioContext);
  if (!inStudioProvider) {
    throw new Error('useStudio must be used within a StudioProvider');
  }

  const location = useLocation();
  const navigate = useNavigate();

  const isStudioOpen = isStudioPath(location.pathname);

  const openStudio = useCallback(() => {
    const studioPath = getSectionPath(SectionType.STUDIO);
    if (location.pathname === studioPath) return;
    navigate(studioPath);
  }, [location.pathname, navigate]);

  const closeStudio = useCallback(() => {
    if (!isStudioPath(location.pathname)) return;
    navigate(getSectionPath(SectionType.HOME), { replace: true });
  }, [location.pathname, navigate]);

  return useMemo(() => ({
    closeStudio,
    isStudioOpen,
    openStudio,
  }), [closeStudio, isStudioOpen, openStudio]);
}

export default StudioContext;
